import { useMemo, useRef, useEffect } from 'react'
import { WEEKDAYS, WEEKDAY_COLORS } from './weekColors'

const PAD_FACTOR = 0.15

// Web-mercator-ish projection (good enough at our scale; for production-grade
// accuracy use a real mercator helper). Returns { x, y } in canvas pixels.
function projectLatLng (lat, lng, bounds, w, h) {
  const lngSpan = bounds.maxLng - bounds.minLng
  const latSpan = bounds.maxLat - bounds.minLat
  const x = ((lng - bounds.minLng) / lngSpan) * w
  const y = h - ((lat - bounds.minLat) / latSpan) * h
  return { x, y }
}

function computeBounds (stops, yard) {
  const lats = stops.map(s => s.lat).concat(yard ? [yard.lat] : [])
  const lngs = stops.map(s => s.lng).concat(yard ? [yard.lng] : [])
  if (!lats.length) return null
  let minLat = Math.min(...lats), maxLat = Math.max(...lats)
  let minLng = Math.min(...lngs), maxLng = Math.max(...lngs)
  const latPad = (maxLat - minLat) * PAD_FACTOR || 0.01
  const lngPad = (maxLng - minLng) * PAD_FACTOR || 0.01
  return {
    minLat: minLat - latPad, maxLat: maxLat + latPad,
    minLng: minLng - lngPad, maxLng: maxLng + lngPad
  }
}

function DayTile ({ dayIndex, dayGroup, yard, bounds }) {
  const canvasRef = useRef(null)
  const color = WEEKDAY_COLORS[dayIndex]

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !bounds) return
    const w = canvas.width = canvas.clientWidth
    const h = canvas.height = canvas.clientHeight
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, w, h)

    // Background: faint grid
    ctx.strokeStyle = 'rgba(0,0,0,0.06)'
    ctx.lineWidth = 1
    for (let i = 0; i < 8; i++) {
      const x = (i / 8) * w
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke()
      const y = (i / 8) * h
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke()
    }

    const stops = dayGroup?.stops || []
    if (stops.length === 0) return

    // Polyline
    ctx.strokeStyle = color
    ctx.lineWidth = 2
    ctx.beginPath()
    if (yard) {
      const yp = projectLatLng(yard.lat, yard.lng, bounds, w, h)
      ctx.moveTo(yp.x, yp.y)
      for (const s of stops) {
        const p = projectLatLng(s.lat, s.lng, bounds, w, h)
        ctx.lineTo(p.x, p.y)
      }
      ctx.stroke()
    }

    // Stop markers
    for (const s of stops) {
      const p = projectLatLng(s.lat, s.lng, bounds, w, h)
      ctx.beginPath()
      ctx.arc(p.x, p.y, 5, 0, 2 * Math.PI)
      ctx.fillStyle = color
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.stroke()
    }

    // Yard marker
    if (yard) {
      const yp = projectLatLng(yard.lat, yard.lng, bounds, w, h)
      ctx.beginPath()
      ctx.arc(yp.x, yp.y, 6, 0, 2 * Math.PI)
      ctx.fillStyle = '#1c2419'
      ctx.fill()
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 9px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Y', yp.x, yp.y + 3)
    }
  }, [dayGroup, yard, bounds, color])

  return (
    <div style={{
      position: 'relative', border: `2px solid ${color}`, borderRadius: 12,
      overflow: 'hidden', background: 'linear-gradient(135deg, #d6c9a8, #b5a47e)'
    }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      <div style={{
        position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center',
        gap: 6, background: 'rgba(251,248,241,0.94)', borderRadius: 999,
        padding: '4px 10px', boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
      }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
        <span style={{ fontSize: 12, fontWeight: 800, color: '#1c2419' }}>{WEEKDAYS[dayIndex]?.full}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#97a094' }}>{dayGroup?.stops?.length || 0}</span>
      </div>
    </div>
  )
}

export default function WeekGridView ({ dayGroups, yard }) {
  // Auto-fit bounds across ALL visible stops + yard
  const allStops = useMemo(() => dayGroups.flatMap(g => g.stops || []), [dayGroups])
  const bounds = useMemo(() => computeBounds(allStops, yard), [allStops, yard])

  // Build a map from dayIndex → group (collapse multi-crew groups into one tile)
  const groupsByDay = useMemo(() => {
    const m = new Map()
    for (const g of dayGroups) {
      const existing = m.get(g.dayIndex)
      if (existing) {
        existing.stops = existing.stops.concat(g.stops)
      } else {
        m.set(g.dayIndex, { dayIndex: g.dayIndex, stops: [...g.stops] })
      }
    }
    return m
  }, [dayGroups])

  return (
    <div style={{
      position: 'absolute', inset: 0, padding: 14, background: '#efe9da',
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: 'repeat(2, 1fr)', gap: 12, overflow: 'hidden', zIndex: 500
    }}>
      {[0, 1, 2, 3, 4].map(i => (
        <DayTile key={i} dayIndex={i} dayGroup={groupsByDay.get(i)} yard={yard} bounds={bounds} />
      ))}
      <div style={{
        borderRadius: 12, border: '1.5px dashed #cec3a5',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 6, color: '#8a8472', padding: 16, textAlign: 'center'
      }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#6f7a6b' }}>Same area, different days?</div>
        <div style={{ fontSize: 11.5, maxWidth: 180, lineHeight: 1.45 }}>
          Compare the five maps — clusters that repeat are territory candidates.
        </div>
      </div>
    </div>
  )
}
